import {
  getVisitorId as amalgamGetVisitorId,
  getDomainTag,
  getBase64DomainTag,
  getVisitorFromAmalgam,
  clearVisitorIdFromAmalgam
} from './Amalgam';
import { getDatabaseRef, updateDatabaseRef } from './Firebase';
import { getCookie, removeCookie } from './LocalStorage';
import {
  getSkeletonVisitor,
  updateSkeletonVisitor,
  createOrGetVisitor as apiCreateOrGetVisitor,
  deleteVisitorAccount
} from './Api';
import { isMobile } from './Utils';
import { getShowcaseSlug, shouldRequestIdentity } from './Showcase';
import { shouldVerifyOTP } from './Otp';
import { orderFinalizeQueue } from './queue';
import {
  didInterruptIdentityRequest,
  getIdentity,
  identityComplete,
  identityRequested,
  setShippingAddressDetails
} from './Identity';
import { getRequestIdentityTime } from './Experiments';
import EventBus from './EventBus';
import { initializeCompanyFacebookPixels, initializeCompanyGoogleAnalytics } from './Events';

let _visitor;
let sideEffectsPromise = null;
let sideEffectsResolved = false;
const shippingAddressId = null;
let requestIdentityTimer = null;
let timeToRequestIdentityPromise = null;

export const getCachedVisitor = () => _visitor || getVisitorFromAmalgam();
export const getVisitorId = () => (_visitor ? _visitor.id : amalgamGetVisitorId());
export const getVisitorPhoneVerified = () => (getCachedVisitor() || {}).phoneVerified;

const setVisitorDomainTag = (visitorId, { domainTag, base64DomainTag }) => {
  if (!domainTag || !base64DomainTag) {
    return;
  }

  const updates = {};

  if (domainTag.qs_phone_number) {
    updates[`phones/${base64DomainTag.qs_customer_phone}`] = {
      countryCode: domainTag.qs_phone_country,
      number: domainTag.qs_phone_number
    };
  }

  if (domainTag.qs_customer_name) {
    updates[`names/${base64DomainTag.qs_customer_name}`] = domainTag.qs_customer_name;
  }

  const ref = getDatabaseRef(`global-visitor-domainTag/${visitorId}`);
  updateDatabaseRef(ref, updates).catch(() => {});
};

export const processVisitorSideEffects = (visitor, { domainTag, base64DomainTag }) => {
  if (sideEffectsResolved) {
    return new Promise((resolve) => resolve(_visitor));
  }

  if (sideEffectsPromise) {
    return sideEffectsPromise;
  }

  sideEffectsPromise = new Promise((resolve) => {
    setVisitorDomainTag(visitor.id, { domainTag, base64DomainTag });

    const oldVisitorId = getCookie('_old_visitor_id');
    removeCookie('_old_visitor_id');

    if (!oldVisitorId) {
      sideEffectsResolved = true;
      return resolve(visitor);
    }

    getSkeletonVisitor(oldVisitorId)
      .then((oldVisitor) => {
        if (!oldVisitor) {
          sideEffectsResolved = true;
          return resolve(visitor);
        }

        const updates = {};
        if (oldVisitor.name && !visitor.name) {
          updates.name = oldVisitor.name;
        }

        if (oldVisitor.phone && !visitor.phone) {
          updates.phone = oldVisitor.phone;
        }

        if (oldVisitor.email && !visitor.email) {
          updates.email = oldVisitor.email;
        }

        if (!Object.keys(updates).length) {
          sideEffectsResolved = true;
          return resolve(visitor);
        }

        return updateSkeletonVisitor(updates);
      })
      .then((newVisitor) => {
        _visitor = newVisitor;

        sideEffectsResolved = true;
        resolve(_visitor);
      })
      .catch(() => {
        sideEffectsResolved = true;
        resolve(visitor);
      });
  });

  return sideEffectsPromise;
};

export const createOrGetVisitor = (visitorId) => {
  const savedVisitorId = visitorId || getVisitorId();
  const domainTag = getDomainTag();
  const base64DomainTag = getBase64DomainTag();

  return apiCreateOrGetVisitor({ savedVisitorId }).then((visitor) => {
    _visitor = visitor;
    initializeCompanyFacebookPixels(visitor);
    initializeCompanyGoogleAnalytics(visitor);

    return processVisitorSideEffects(visitor, { domainTag, base64DomainTag });
  });
};

export const logoutVisitor = () =>
  new Promise((resolve, reject) => {
    try {
      const visitorIdCookieKey = '_visitor_id';

      removeCookie(visitorIdCookieKey);
      removeCookie('_auth_token');
      removeCookie('_old_visitor_id');
      clearVisitorIdFromAmalgam();
      _visitor = null;

      updateSkeletonVisitor({ phoneVerified: false }).then((visitor) => {
        if (visitor.phoneVerified) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject('visitor id not cleared');
        }
        window.location.reload();
        resolve();
      });
    } catch (e) {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject(e);
    }
  });

export const getVisitorCountry = () =>
  createOrGetVisitor().then((visitor) => (visitor ? visitor.getCountryCode() : null));

export const getDownloadLink = (options) => {
  const forceInfo = !!(options || {}).forceInfo;

  let downloadLink;

  const phone = _visitor.phone;

  if (!isMobile()) {
    downloadLink = `https://web.quicksell.co/catalogues?showcaseSlug=${getShowcaseSlug()}&source=quicksell.co&qs_origin=/resell-download/${getShowcaseSlug()}&qs_origin_phone=${
      phone || ''
    }`;
  } else if (forceInfo) {
    downloadLink = `https://link.quicksell.co/resell-info/${getShowcaseSlug()}`;
  } else {
    downloadLink = `https://link.quicksell.co/resell/${getShowcaseSlug()}`;
  }

  return downloadLink;
};

const timedUpdateShippingAddress = ({ updates }) =>
  new Promise((resolve) => {
    if (
      !updates.addressLineOne &&
      !updates.addressLineTwo &&
      !updates.city &&
      !updates.country &&
      !updates.landmark &&
      !updates.pincode &&
      !updates.state &&
      !updates.countryId &&
      !updates.stateId &&
      !updates.cityId
    ) {
      resolve();
      return;
    }

    setShippingAddressDetails(updates);
    resolve();
  });

export const getCachedShippingAddressId = () => shippingAddressId;

export const pushTimedUpdateShippingAddress = ({ updates }) => {
  orderFinalizeQueue.push({
    actionFn: timedUpdateShippingAddress,
    params: [{ updates }]
  });
};

const timeToRequestIdentity = () => {
  if (timeToRequestIdentityPromise) {
    return;
  }

  timeToRequestIdentityPromise = getIdentity().then((identity) => {
    if (
      !identity ||
      !identity.name ||
      !identity.phone ||
      (shouldVerifyOTP() && !identity.phoneVerified)
    ) {
      identityRequested();

      EventBus.notify(EventBus.TYPE.HIDE_REQUEST_IDENTITY_MODAL);
      EventBus.notify(EventBus.TYPE.SHOW_REQUEST_IDENTITY_CHAT, {
        onDone: () => {
          identityComplete();
          timeToRequestIdentityPromise = null;
        },
        onCancel: () => {}
      });
    }
  });
};

export const initializeRequestIdentity = () => {
  if (!shouldRequestIdentity()) {
    return;
  }

  if (didInterruptIdentityRequest()) {
    timeToRequestIdentity();
    return;
  }

  let time = 15000;
  if (getRequestIdentityTime()) {
    time = getRequestIdentityTime() * 1000;
  }

  requestIdentityTimer = setTimeout(() => {
    timeToRequestIdentity();
  }, time);
};

export const cancelRequestIdentity = () => {
  if (requestIdentityTimer) {
    clearTimeout(requestIdentityTimer);
    requestIdentityTimer = null;
  }
};

export const deleteVisitorAccountAndLogout = () =>
  deleteVisitorAccount().then(() => logoutVisitor());
